import { Link } from 'react-router-dom';
import Card from '../../../components/ui/Card';
import { HexLockup } from '../../../components/FieldCertLogo';
import LandingNav from './LandingNav';
import LandingHero from './LandingHero';
import { LANDING_CONTENT } from '../content';

const { capabilities, features, howItWorks, cta, footer, nav } = LANDING_CONTENT;

function CapabilityStrip() {
  return (
    <div className="landing-strip">
      <ul className="landing-container landing-strip-inner">
        {capabilities.map((item) => (
          <li key={item} className="landing-strip-item">
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Features() {
  return (
    <section id="features" className="landing-band">
      <div className="landing-container">
        <p className="landing-eyebrow">{features.eyebrow}</p>
        <h2 className="landing-section-title">{features.title}</h2>

        <div className="landing-feature-grid">
          {features.items.map(({ icon: Icon, title, body }) => (
            <Card key={title} className="landing-feature-card">
              <span className="landing-feature-icon" aria-hidden="true">
                <Icon size={20} />
              </span>
              <h3 className="landing-feature-title">{title}</h3>
              <p className="landing-feature-body">{body}</p>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section id="how-it-works" className="landing-band landing-band--tinted">
      <div className="landing-container">
        <p className="landing-eyebrow">{howItWorks.eyebrow}</p>
        <h2 className="landing-section-title">{howItWorks.title}</h2>

        <ol className="landing-steps">
          {howItWorks.steps.map(({ number, title, body }) => (
            <li key={number} className="landing-step">
              <span className="landing-step-number">{number}</span>
              <h3 className="landing-step-title">{title}</h3>
              <p className="landing-step-body">{body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function CallToAction() {
  return (
    <section className="landing-band">
      <div className="landing-container">
        <div className="landing-cta">
          <h2 className="landing-section-title landing-cta-title">{cta.title}</h2>
          <p className="landing-cta-body">{cta.body}</p>
          <Link to="/login" className="btn btn-primary">
            {cta.button}
          </Link>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="landing-footer">
      <div className="landing-container landing-footer-inner">
        <div className="landing-footer-brand">
          <HexLockup iconSize={30} wmSize="xs" gap={10} light />
          <p className="landing-footer-blurb">{footer.blurb}</p>
        </div>

        <nav className="landing-footer-links" aria-label="Page sections">
          {nav.anchors.map(({ id, label }) => (
            <a key={id} className="landing-nav-link" href={`#${id}`}>
              {label}
            </a>
          ))}
        </nav>

        <p className="landing-footer-copy">
          © {new Date().getFullYear()} {footer.copyright}
        </p>
      </div>
    </footer>
  );
}

/**
 * Public marketing page at /welcome.
 *
 * `data-theme="light"` is set on the wrapper rather than on <html> so the page
 * always renders light without fighting ThemeContext: the token blocks in
 * _tokens.css are plain attribute selectors, so the whole subtree inherits the
 * light palette while the rest of the app keeps the user's chosen theme.
 */
export default function LandingPage() {
  return (
    <div className="landing" data-theme="light">
      <LandingNav />
      <main>
        <LandingHero />
        <CapabilityStrip />
        <Features />
        <HowItWorks />
        <CallToAction />
      </main>
      <Footer />
    </div>
  );
}
